import { createContext, useContext, useState } from "react";
import axios from "../api/axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const TOKEN_KEY = "token";
const ROLE_KEY = "role";
const ORGANIZATION_KEY = "organization";
const REFERENCE_TOKEN = "reference_token";
const MODULE_ACCESS_KEY = "moduleAccess";

const FetchContext = createContext(null);

const removeToken = () => {
  sessionStorage.removeItem(TOKEN_KEY);
};

const FetchProvider = ({ children }) => {
  const navigate = useNavigate();

  const saveToken = (token, role) => {
    const roles = role.filter((item) => item.name !== "corporate");
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(ROLE_KEY, roles[0].name);
    setRoleType(roles[0].name);
  }

  const saveOrganization = (organization) => {
    sessionStorage.setItem(ORGANIZATION_KEY, organization);
    setOrganization(organization);
  };

  const saveModulesAccess = (modules) => {
    sessionStorage.setItem(MODULE_ACCESS_KEY, modules);
    setModulesAccess(modules);
  }

  const saveReferenceToken = (refToken) => {
    sessionStorage.setItem(REFERENCE_TOKEN, refToken);
    setReferenceToken(refToken);
  }

  const saveRole = (role) => {
    sessionStorage.setItem(ROLE_KEY, role);
  };

  const getToken = () => sessionStorage.getItem(TOKEN_KEY);
  const getRoles = () => sessionStorage.getItem(ROLE_KEY);
  const getOrganizationId = () => sessionStorage.getItem(ORGANIZATION_KEY);
  const getReferenceToken = () => sessionStorage.getItem(REFERENCE_TOKEN);

  const [organization, setOrganization] = useState(getOrganizationId());
  const [modulesAccess, setModulesAccess] = useState(sessionStorage.getItem(MODULE_ACCESS_KEY));
  const [roleType, setRoleType] = useState(getRoles());
  // eslint-disable-next-line
  const [token, setToken] = useState(getToken());
  const [referenceToken, setReferenceToken] = useState(getReferenceToken());

  const authRequest = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
  });

  authRequest.interceptors.request.use(
    (config) => {
      const contentType = config.headers['Content-Type'];
      const refToken = sessionStorage.getItem("reference_token");
      const token = sessionStorage.getItem("token");
      if (refToken){
        return {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'application/json',
            'X-Reference-Token': refToken,
            Accept: '*/*',
          },
        };
      }
  
      if (contentType === 'multipart/form-data') {
        return {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data',
            Accept: '*/*',
            'X-Current-Organization': organization,
            Authorization: `Bearer ${token}`,
          },
        };
      }
  
      return {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'application/json',
          Accept: '*/*',
          'X-Current-Organization': organization,
          Authorization: `Bearer ${token}`,
        },
      };
    },
    (error) => Promise.reject(error)
  );

  authRequest.interceptors.response.use(
    (response) => {
      const token = response.data.access;
      if (token) {
        setToken(token);
        authRequest.defaults.headers["Authorization"] = token;
      }
      return response;
    },
    (error) => {
      if (error.response && error.response.status === 401) {
        sessionStorage.clear();
        localStorage.clear();
        navigate("/");
      } else if (error.message === "Network Error") {
        toast.warning("Network Error, verify your Internet connection");
      } else {
        return Promise.reject(error);
      }
    }
  );

  return (
    <FetchContext.Provider
      value={{
        authRequest,
        removeToken,
        saveToken,
        saveOrganization,
        saveRole,
        organization: organization || "",
        setOrganization,
        getToken,
        getRoles,
        getOrganizationId,
        roleType: roleType || "",
        setRoleType,
        saveReferenceToken,
        getReferenceToken,
        referenceToken,
        modulesAccess: modulesAccess || "",
        saveModulesAccess,
      }}
    >
      {children}
    </FetchContext.Provider>
  );
};

const useFetch = () => {
  const context = useContext(FetchContext);
  if (!context) {
    throw new Error("useFetch must be used within a FetchProvider");
  }

  return context;
};
export { FetchProvider, useFetch };
